import { Component, Injectable, OnInit } from '@angular/core';
import { DatePipe, ViewportScroller } from '@angular/common';
import { Router } from '@angular/router';
import { ITranslations } from './_models/ITranslations';
import { DomSanitizer } from '@angular/platform-browser';
import { CookieService } from 'ngx-cookie-service';
import { UtilService } from './_services/util.service';
import { CommunicationService } from './_services/communication.service';
import { ITranslationsClass } from './_models/ITranslationsClass';
import { ClipboardService } from 'ngx-clipboard';
import DxThemes from 'devextreme/ui/themes';
import { MsalService } from '@azure/msal-angular';
import { LoginService } from './_services/login.service';
import { LicensingService } from './_services/licensing.service';

@Injectable({ providedIn: 'root' })
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit {
  //#region Fields
  dismiss: string;
  type: string;
  message: string;
  icon: string;
  showLoaderModal: boolean = false;
  nodeId: string;
  userId: string;
  username: string;
  darkModeSetting: boolean = false;
  translations: ITranslations;
  siteName: string;
  userRole: string;
  translationsClass: ITranslationsClass = new ITranslationsClass;
  loaderText: string;
  //#endregion

  constructor(
    public router: Router,
    public datePipe: DatePipe,
    public domSanitizer: DomSanitizer,
    public cookieService: CookieService,
    public clipboard: ClipboardService,
    private scroll: ViewportScroller,
    private utilService: UtilService,
    private communicationService: CommunicationService,
    private msal: MsalService,
    private loginService: LoginService,
    private licensingService: LicensingService,
  ) {
    this.communicationService.mod_app_showError$.subscribe((refNumber) => {
      this.alertError(`${this.translations ? this.translations.ALERT_Error_ErrorWithRefNumberOccured : 'Error: '} ${refNumber}`);
      this.hideLoader();
    })

    if (localStorage.getItem('qx_setting_darkMode')) this.darkModeSetting = true;
    DxThemes.current(this.darkModeSetting ? 'generic.dark' : 'generic.light');
  }

  async ngOnInit(): Promise<void> {
    await this.msal.instance.initialize();
    const result = await this.msal.instance.handleRedirectPromise();
    if (result !== null) {
      this.showLoader();
      this.msal.instance.setActiveAccount(result.account);
      let acc = this.msal.instance.getAllAccounts()[0];
      localStorage.setItem('msal.acc', JSON.stringify(acc));
      let formData = this.buildForm(['homeAccountId', 'email'], [acc.homeAccountId, acc.username]);
      this.loginService.loginFromMicrosoft(formData).subscribe({
        next: (data: any) => {
          localStorage.setItem('qx_userId', data.userId);
          localStorage.setItem('qx_userRole', data.userRole);
          localStorage.setItem('qx_username', data.username);

          if (!localStorage.getItem('sessionId')) {
            let stationId = localStorage.getItem('stationId');
            let formData = this.buildForm(['userId', 'stationId'], [data.userId, stationId]);
            this.licensingService.createSession(formData).subscribe({
              next: (sessionData: { result: boolean, message: string, sessionId: string, stationId: string }) => {
                if (sessionData.message.includes('ERROR')) {
                  this.alertError(sessionData.message);
                  return;
                }

                if (sessionData.result) {
                  localStorage.setItem('sessionId', sessionData.sessionId);
                  localStorage.setItem('stationId', sessionData.stationId);
                }

                this.hideLoader();
                this.router.navigate(['base']);
              }, error: (errorLog) => { console.log(errorLog); }
            })
          }
        }, error: (errorLog) => { console.log(errorLog); }
      })
    }

    setTimeout(() => this.dismiss = 'slideOutRight', 500);
    this.utilService.getSiteName().subscribe({
      next: (data: { result: string }) => {
        this.siteName = data.result;
        this.getTranslations();
      }, error: (errorLog) => { console.log(errorLog); }
    })
  }

  getTranslations(): void {
    let userId = localStorage.getItem('qx_userId') ? Number(localStorage.getItem('qx_userId')) : -1;
    let formData = this.buildForm(['userId'], [userId.toString()]);
    this.utilService.getTranslations(formData).subscribe({
      next: (data) => {
        Object.keys(this.translationsClass).forEach(transkey => {
          if (data.hasOwnProperty(transkey) && this.translationsClass[transkey] !== data[transkey]) {
            this.translationsClass[transkey] = data[transkey];
          }
        });

        this.translations = this.translationsClass;
        this.loaderText = 'loading...';
        this.communicationService.translationLoaded();
      }, error: (errorLog) => {
        console.log(errorLog);
        this.translations = this.translationsClass;
        this.communicationService.translationLoaded();
      }
    })
  }

  alertSuccess(message: string): void {
    this.type = 'success';
    this.message = message;
    this.icon = 'circle-check';
    setTimeout(() => this.dismiss = 'slideOutRight', 2000);
    this.dismiss = '';
  }

  alertError(message: string): void {
    this.type = 'danger';
    this.message = message;
    this.icon = 'circle-xmark';
    setTimeout(() => this.dismiss = 'slideOutRight', 3000);
    this.dismiss = '';
  }

  alertInfo(message: string): void {
    this.type = 'info';
    this.message = message;
    this.icon = 'circle-info';
    setTimeout(() => this.dismiss = 'slideOutRight', 2000);
    this.dismiss = '';
  }

  showLoader(): void {
    this.scroll.scrollToPosition([0, 0]);
    this.showLoaderModal = true;
  }

  hideLoader(): void {
    this.showLoaderModal = false;
    this.loaderText = 'loading...';
  }

  buildForm(keys: string[], values: string[]): FormData {
    let formData = new FormData();
    keys.forEach((key, i) => {
      formData.append(key, encodeURIComponent(values[i] == null ? null : values[i].toString()));
    })
    return formData;
  }

  tryParseBoolean(value: any): any {
    if (typeof value === 'boolean') return value;

    if (typeof value === 'string') {
      let lowercaseValue = value.trim().toLowerCase();
      if (lowercaseValue === 'true' || lowercaseValue === '1') return true;
      if (lowercaseValue === 'false' || lowercaseValue === '0') return false;
    }

    return value;
  }

  getFormattedImage(imageData: string): string {
    let format = imageData.charAt(0) == '/' ? 'jpg' : 'png';
    if (imageData.includes(`;base64`)) return imageData;
    return `data:image/${format};base64, ${imageData}`;
  }
}
